/*
 * Smores Reviews — client renderer for the hybrid Judge.me section.
 *
 * Data source priority:
 *   1. demo JSON  (<script data-demo-data> — only when "Use demo data" is on)
 *   2. window.jdgm.data.reviewWidget[productId]  (verbatim contents of
 *      product.metafields.judgeme.review_widget_data, hydrated by Judge.me)
 *
 * The exact shape of Judge.me's review_widget_data is UNDOCUMENTED and
 * version-dependent, so every field is read defensively across the names
 * it has been seen under. If Judge.me changes the shape, adjust the
 * candidate lists in pick()/extractReviews() — that's the only place that
 * should need touching. Verify the live shape once via:
 *   console.log(window.jdgm.data.reviewWidget[<product-id>])
 *
 * The cards' histogram bars are scaled by count ÷ total ratings (the
 * standard "what share of reviewers gave N stars" reading).
 *
 * All review text is user-generated: it is inserted with textContent /
 * element creation only — never innerHTML — so it cannot inject markup.
 */
class SmoresReviews extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId;
    this.perPage = parseInt(this.dataset.perPage, 10) || 8;
    this.isDemo = this.dataset.demo === 'true';

    this.grid = this.querySelector('[data-grid]');
    this.sortEl = this.querySelector('[data-sort]');
    this.histogramEl = this.querySelector('[data-histogram]');
    this.loadingEl = this.querySelector('[data-loading]');
    this.emptyEl = this.querySelector('[data-empty]');
    this.loadMoreEl = this.querySelector('[data-loadmore]');
    this.countEl = this.querySelector('[data-rating-count]');
    this.writeEl = this.querySelector('[data-write]');
    if (!this.grid) return;

    this.shown = this.perPage;
    this.sortMode = 'recent';

    if (this.sortEl) {
      this.sortEl.addEventListener('change', () => {
        this.sortMode = this.sortEl.value;
        this.shown = this.perPage;
        this.renderCards();
      });
    }
    if (this.loadMoreEl) {
      this.loadMoreEl.addEventListener('click', () => {
        this.shown += this.perPage;
        this.renderCards();
      });
    }

    this.setupWriteButton();
    this.load();
  }

  /* ── Write-a-review → Judge.me's native form modal ───────────
     We render the widget ourselves, but the review FORM stays Judge.me's.
     Judge.me only wires its form to the write button inside its own
     (hidden, off-screen) widget, so we (a) make sure that widget is
     initialized and (b) forward our button's click to its native trigger.
     Supports the revamp widget (.jm-action-buttons__button) and the
     legacy widget (.jdgm-write-rev-link). */
  setupWriteButton() {
    if (!this.writeEl) return;
    // Render Judge.me's hidden widget now so its form handlers exist before
    // the first click (no-op until Judge.me's script has loaded).
    this.ensureJudgemeWidget();
    this.writeEl.addEventListener('click', (e) => {
      e.preventDefault();
      this.openReviewForm();
    });
  }

  ensureJudgemeWidget(tries = 0) {
    const jdgm = window.jdgm;
    if (jdgm && typeof jdgm.initializeWidgets === 'function') {
      try { jdgm.initializeWidgets(); } catch (e) { /* noop */ }
      return;
    }
    // Judge.me's app-embed script loads async — wait up to ~15s for it.
    if (tries < 60) setTimeout(() => this.ensureJudgemeWidget(tries + 1), 250);
  }

  nativeWriteTrigger() {
    const host = this.querySelector('.smores-reviews__jdgm-host');
    if (!host) return null;
    // Revamp widget: primary action button labelled "Write a review".
    const revamp = [...host.querySelectorAll('.jm-action-buttons__button')]
      .find((b) => /\bwrite\b/i.test(b.textContent)) ||
      host.querySelector('.jm-action-buttons__button.jm-button--primary');
    // Legacy widget: the classic link. Scoped to the host so we never
    // re-trigger our own footer button.
    return revamp || host.querySelector('.jdgm-write-rev-link');
  }

  openReviewForm(tries = 0) {
    const trigger = this.nativeWriteTrigger();
    if (trigger) { trigger.click(); return; }
    // Widget hasn't rendered yet — kick initialization and retry briefly.
    this.ensureJudgemeWidget();
    if (tries < 25) { setTimeout(() => this.openReviewForm(tries + 1), 200); return; }
    console.warn('[smores-reviews] Judge.me write-review trigger not found — is the Judge.me app embed enabled?');
  }

  /* ── Data loading ────────────────────────────────────────── */

  async load() {
    let data = this.isDemo ? this.readDemo() : await this.readJudgeme();
    data = data || {};

    this.reviews = this.extractReviews(data);
    this.histogram = this.getHistogram(data, this.reviews);
    this.summary = this.getSummary(data, this.reviews);

    this.updateSummary();
    this.renderHistogram();
    this.renderCards();

    if (this.loadingEl) this.loadingEl.hidden = true;
    this.grid.setAttribute('aria-busy', 'false');
    if (this.emptyEl) this.emptyEl.hidden = this.reviews.length > 0;
  }

  readDemo() {
    const el = this.querySelector('[data-demo-data]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /* Poll briefly for Judge.me's hydrated data (its script loads async). */
  readJudgeme() {
    const id = this.productId;
    const get = () => window.jdgm && window.jdgm.data &&
      window.jdgm.data.reviewWidget && window.jdgm.data.reviewWidget[id];
    return new Promise((resolve) => {
      const found = get();
      if (found) return resolve(found);
      let tries = 0;
      const timer = setInterval(() => {
        const d = get();
        if (d || ++tries > 50) { clearInterval(timer); resolve(d || null); }
      }, 100); // up to ~5s
    });
  }

  /* ── Defensive field extraction ──────────────────────────── */

  pick(obj, keys, fallback) {
    for (const k of keys) {
      if (obj && obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
    }
    return fallback;
  }

  extractReviews(data) {
    // The reviews array may be at the top level or nested under a key.
    let arr = this.pick(data, ['reviews', 'reviewWidget', 'review_list', 'items'], null);
    if (!Array.isArray(arr)) {
      // Fall back to the first array of objects that look like reviews.
      arr = Object.values(data || {}).find(
        (v) => Array.isArray(v) && v.length && typeof v[0] === 'object'
      ) || [];
    }
    return arr.map((r) => ({
      rating: Number(this.pick(r, ['rating', 'score', 'stars', 'star_rating'], 0)) || 0,
      title: String(this.pick(r, ['title', 'review_title', 'subject'], '')),
      body: String(this.pick(r, ['body', 'content', 'text', 'review', 'message'], '')),
      author: String(this.pick(this.pick(r, ['reviewer'], r) || r,
        ['name', 'author', 'reviewer_name', 'display_name'], 'Anonymous')),
      date: this.pick(r, ['created_at', 'date', 'date_utc', 'reviewed_at', 'updated_at'], ''),
      verified: Boolean(this.pick(r, ['verified_buyer', 'verified', 'is_verified', 'verified_purchase'], false)),
      product: this.productOf(r),
      pictures: this.pick(r, ['pictures', 'pictures_urls', 'photos', 'images'], []) || [],
      avatar: String(this.pick(r, ['avatar_image_url', 'avatar_url', 'avatar', 'photo'], '')),
      initial: String(this.pick(r, ['reviewer_initial'], '')),
    }));
  }

  // Product label for the card pill. Handles the title living directly
  // under a string key (`product`) or nested in a product object.
  productOf(r) {
    const direct = this.pick(r, ['product_title', 'product_name'], '');
    if (direct) return String(direct);
    const p = r.product !== undefined ? r.product : r.product_handle;
    if (typeof p === 'string') return p;
    if (p && typeof p === 'object') return String(this.pick(p, ['title', 'name'], ''));
    return '';
  }

  // Average + total count. Judge.me's review_widget_data exposes
  // average_rating (a string like "3.80") and number_of_reviews; the
  // standard reviews.* metafield is unreliable (often 0), so this is the
  // source of truth. Falls back to computing from loaded reviews.
  getSummary(data, reviews) {
    let average = Number(this.pick(data, ['average_rating', 'average', 'rating', 'avg'], NaN));
    if (isNaN(average)) {
      average = reviews.length
        ? reviews.reduce((s, r) => s + (Number(r.rating) || 0), 0) / reviews.length : 0;
    }
    let count = Number(this.pick(data, ['number_of_reviews', 'count', 'reviews_count', 'total'], NaN));
    if (isNaN(count)) count = reviews.length;
    return { average, count };
  }

  // Per-star distribution. Judge.me ships an ARRAY of
  // { rating, frequency, percentage }; demo data uses an object map
  // { 5: n, ... }; otherwise we compute from loaded reviews (approximate
  // when only the first page is present — a free-plan limit).
  getHistogram(data, reviews) {
    const hist = {};
    for (let s = 1; s <= 5; s++) hist[s] = { count: 0, pct: null };
    const raw = this.pick(data, ['histogram', 'rating_breakdown', 'distribution', 'ratings_breakdown', 'breakdown'], null);
    if (Array.isArray(raw)) {
      raw.forEach((h) => {
        const s = Number(h.rating);
        if (hist[s]) hist[s] = {
          count: Number(h.frequency != null ? h.frequency : h.count) || 0,
          pct: h.percentage != null ? Number(h.percentage) : null,
        };
      });
    } else if (raw && typeof raw === 'object') {
      for (let s = 1; s <= 5; s++) {
        const v = raw[s] !== undefined ? raw[s] : raw[String(s)];
        if (v !== undefined) hist[s].count = Number(v) || 0;
      }
    } else {
      reviews.forEach((r) => { const s = Math.round(r.rating); if (hist[s]) hist[s].count++; });
    }
    const total = Object.values(hist).reduce((a, b) => a + b.count, 0);
    for (let s = 1; s <= 5; s++) if (hist[s].pct == null) hist[s].pct = total ? (hist[s].count / total) * 100 : 0;
    return hist;
  }

  /* ── Rendering ───────────────────────────────────────────── */

  // Override the server-rendered summary with the authoritative data.
  updateSummary() {
    const s = this.summary;
    if (!s) return;
    const avgEl = this.querySelector('.smores-reviews__average');
    if (avgEl && s.average > 0) avgEl.textContent = s.average.toFixed(1);
    const stars = this.querySelector('.smores-reviews__average-stars');
    if (stars) {
      const fill = stars.querySelector('.smores-stars__fill');
      if (fill) fill.style.width = Math.max(0, Math.min(100, (s.average / 5) * 100)) + '%';
      stars.setAttribute('aria-label', s.average.toFixed(1) + ' out of 5 stars');
    }
    if (this.countEl && s.count != null) this.countEl.textContent = s.count;
  }

  renderHistogram() {
    if (!this.histogramEl) return;
    const total = Object.values(this.histogram).reduce((a, b) => a + b.count, 0);
    if (!total) { this.histogramEl.hidden = true; return; }
    this.histogramEl.hidden = false;
    this.histogramEl.removeAttribute('aria-hidden');

    this.histogramEl.querySelectorAll('.smores-reviews__hist-row').forEach((row) => {
      const star = Number(row.dataset.rating);
      const bucket = this.histogram[star] || { count: 0, pct: 0 };
      const fill = row.querySelector('[data-hist-fill]');
      const count = row.querySelector('[data-hist-count]');
      if (fill) fill.style.width = bucket.pct.toFixed(1) + '%';
      if (count) count.textContent = '(' + bucket.count + (bucket.count === 1 ? ' review)' : ' reviews)');
    });
  }

  sortedReviews() {
    const list = this.reviews.slice();
    const time = (r) => { const t = Date.parse(r.date); return isNaN(t) ? 0 : t; };
    switch (this.sortMode) {
      case 'oldest':  return list.sort((a, b) => time(a) - time(b));
      case 'highest': return list.sort((a, b) => b.rating - a.rating || time(b) - time(a));
      case 'lowest':  return list.sort((a, b) => a.rating - b.rating || time(b) - time(a));
      case 'pictures':return list.sort((a, b) => (b.pictures.length > 0) - (a.pictures.length > 0) || time(b) - time(a));
      default:        return list.sort((a, b) => time(b) - time(a)); // recent
    }
  }

  renderCards() {
    if (!this.reviews) return;
    const all = this.sortedReviews();
    const visible = all.slice(0, this.shown);

    this.grid.textContent = '';
    visible.forEach((r) => this.grid.appendChild(this.card(r)));

    if (this.loadMoreEl) this.loadMoreEl.hidden = all.length <= this.shown;
  }

  card(r) {
    const el = document.createElement('article');
    el.className = 'smores-reviews__card';
    el.setAttribute('role', 'listitem');

    // ── Header: avatar + stars · name · date · product pill ──
    const header = document.createElement('header');
    header.className = 'smores-reviews__card-head';

    const avatar = document.createElement('span');
    avatar.className = 'smores-reviews__avatar';
    if (r.avatar) {
      const img = document.createElement('img');
      img.src = r.avatar; img.alt = ''; img.loading = 'lazy';
      avatar.appendChild(img);
    } else {
      avatar.textContent = (r.initial || r.author.trim()[0] || '?').toUpperCase();
      avatar.classList.add('smores-reviews__avatar--initial');
    }
    header.appendChild(avatar);

    const meta = document.createElement('div');
    meta.className = 'smores-reviews__card-meta';
    meta.appendChild(this.stars(r.rating));

    const line = document.createElement('p');
    line.className = 'smores-reviews__card-byline';
    const name = document.createElement('span');
    name.className = 'smores-reviews__card-author';
    name.textContent = r.author;
    line.appendChild(name);
    if (r.date) {
      const dot = document.createElement('span');
      dot.className = 'smores-reviews__card-dot';
      dot.setAttribute('aria-hidden', 'true');
      dot.textContent = '•';
      const date = document.createElement('time');
      date.className = 'smores-reviews__card-date';
      date.textContent = this.formatDate(r.date);
      line.append(dot, date);
    }
    meta.appendChild(line);
    header.appendChild(meta);

    if (r.product) {
      const pill = document.createElement('span');
      pill.className = 'smores-reviews__card-tag';
      pill.textContent = r.product;
      header.appendChild(pill);
    }
    el.appendChild(header);

    // The Figma card shows the product name as the orange pill (above) and
    // relies on the section-level "All reviews verified" badge — so there is
    // no per-card verified pill. r.verified is still parsed for future use.

    if (r.title) {
      const title = document.createElement('h3');
      title.className = 'smores-reviews__card-title';
      title.textContent = r.title;
      el.appendChild(title);
    }
    if (r.body) {
      const body = document.createElement('p');
      body.className = 'smores-reviews__card-body';
      body.textContent = '"' + r.body + '"';
      el.appendChild(body);
    }
    return el;
  }

  stars(rating) {
    const pct = Math.max(0, Math.min(100, (Number(rating) / 5) * 100));
    const wrap = document.createElement('span');
    wrap.className = 'smores-stars smores-reviews__card-stars';
    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', rating + ' out of 5 stars');
    const track = document.createElement('span');
    track.className = 'smores-stars__track';
    track.setAttribute('aria-hidden', 'true');
    track.textContent = '★★★★★';
    const fill = document.createElement('span');
    fill.className = 'smores-stars__fill';
    fill.setAttribute('aria-hidden', 'true');
    fill.style.width = pct + '%';
    fill.textContent = '★★★★★';
    wrap.append(track, fill);
    return wrap;
  }

  formatDate(value) {
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    return d.getDate() + ' ' + months[d.getMonth()] + ', ' + d.getFullYear();
  }
}

if (!customElements.get('smores-reviews')) {
  customElements.define('smores-reviews', SmoresReviews);
}